import PropTypes from 'prop-types';
import { Component } from 'react';
import { connect } from 'react-redux';
import Broadcasts from '../components/Broadcasts';
import {
  broadcastsDataShape,
  channelItemShape,
  routerHistoryShape,
  routerLocationShape,
} from '../helpers/propTypeShapes';
import { setAuthToken } from '../store';
import '../style/dashboard.css';

class Dashboard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      env: 'stage',
      menuEnv: 'default',
      studioURLOverride: null,
    };

    this.handleShopifyLogin = this.handleShopifyLogin.bind(this);
    this.handleStudioURLChange = this.handleStudioURLChange.bind(this);
  }

  componentDidMount() {
    const queryParams = new URLSearchParams(document.location.search);

    // When using guest links, the URL will direct to this page.
    // We check if a broadcast query parameter is defined and redirect to the Join Broadcast page.
    if (queryParams.has('broadcast')) {
      const broadcastHash = queryParams.get('broadcast') ?? '';
      const guestHash = queryParams.get('claim') ?? '';
      this.props.history.push(`/join/${broadcastHash}/${guestHash}`);
      return;
    }

    sessionStorage.setItem('studioURLOverride', null);
    localStorage.setItem('studioURLOverride', null);
    sessionStorage.setItem('studioWindowType', 'window');
    localStorage.setItem('studioWindowType', 'window');
    if (queryParams.get('referrer') === 'sellers') {
      this.handleShopifyLogin();
    } else {
      this.props.checkAuthorization('dashboard');
    }
  }

  handleStudioURLChange(envData) {
    this.setState(envData);
    sessionStorage.setItem('studioURLOverride', envData.studioURLOverride || null);
    localStorage.setItem('studioURLOverride', envData.studioURLOverride || null);
    sessionStorage.setItem('studioWindowType', envData.studioWindowType);
    localStorage.setItem('studioWindowType', envData.studioWindowType);
  }

  handleShopifyLogin() {
    const authToken = this.props.getCookieValue('authToken');
    const expiry = Date.now() + 604800000;
    const params = {
      method: 'post',
      headers: {
        'Content-Type': 'application/json',
        'X-API-VERSION': '2.0',
        authorization: authToken,
      },
    };
    let userObject = {};

    fetch(`${this.props.APIBaseURL}/auth/me`, params)
      .then((results) => results.json())
      .then((data) => {
        if (typeof data.status === 'undefined') {
          const accountParams = {
            headers: {
              authorization: authToken,
            },
          };

          if (data.length > 0) {
            userObject = {
              principal: {
                authorities: data.authorities,
                createdAt: data.createdAt,
                email: data.email,
                enabled: data.enabled,
                id: data.id,
                location: data.location,
                name: data.name,
                username: data.username,
              },
            };
            this.props.setGlobalUserData(userObject);
          }
          this.props.setAuthToken({ authToken, trigger: 'Dashboard@handleShopifyLogin' });
          sessionStorage.setItem('authToken', authToken);
          sessionStorage.setItem('expiry', expiry);
          sessionStorage.setItem('userId', data.id);
          sessionStorage.setItem('userName', data.name);
          sessionStorage.setItem('globalUserName', data.username);

          localStorage.setItem('authToken', authToken);
          localStorage.setItem('expiry', expiry);
          localStorage.setItem('userId', data.id);
          localStorage.setItem('userName', data.name);
          localStorage.setItem('globalUserName', data.username);

          fetch(`${this.props.APIBaseURL}/accounts/search/my-access?projection=detail`, accountParams)
            .then((response) => response.json())
            .then((responseData) => {
              const searchParams = new URLSearchParams(this.props.location.search);
              // eslint-disable-next-line no-underscore-dangle
              const { accounts } = responseData._embedded;
              let accountId = searchParams.get('account_id');
              let accountIndex = '';

              if (accountId !== null) {
                accountId = parseInt(searchParams.get('account_id'), 10);
              }

              if (accountId) {
                for (let i = 0; i < accounts.length; i += 1) {
                  if (accounts[i].id === accountId) {
                    accountIndex = i;
                  }
                }
                if (accountIndex !== '') {
                  const selectedAccount = accounts[accountIndex];

                  this.props.setGlobalAccountData(responseData);
                  sessionStorage.setItem('accounts', responseData);
                  localStorage.setItem('accounts', responseData);
                  this.props.setGlobalAccountId(selectedAccount.id, true, selectedAccount.role);
                  this.props.setGlobalAccountName(selectedAccount.name);
                  this.props.setGlobalAccountRole(selectedAccount.accountUsers.role);
                }
              } else {
                const selectedAccount = accounts[0];

                this.props.setGlobalAccountData(responseData);
                this.props.setGlobalAccountId(selectedAccount.id, true, selectedAccount.role);
                this.props.setGlobalAccountName(selectedAccount.name);
                this.props.setGlobalAccountRole(selectedAccount.accountUsers.role);
              }
              // eslint-disable-next-line no-underscore-dangle
              sessionStorage.setItem('accounts', JSON.stringify(responseData._embedded.accounts));
              if (this.state.keepLoggedIn) {
                // eslint-disable-next-line no-underscore-dangle
                localStorage.setItem('accounts', JSON.stringify(responseData._embedded.accounts));
              }
            })
            .then(() => {
              document.cookie =
                'staging-authToken=;domain=.socialive.us;path=/;expires=;expires=Thu, 01 Jan 1970 00:00:01 GMT';
              const authorized = this.props.checkAuthorization('dashboard');
              if (!authorized) {
                return;
              }

              this.props.trackEvent('Login - Shopify');
              this.props.history.push('/dashboard');
            })
            .catch((error) => {
              // eslint-disable-next-line no-console
              console.error('search/my-access API call failed. ', error);
            });
        }
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('auth/me API call failed. ', error);
      });
  }

  render() {
    let main = <div />;
    if (this.props.componentReady) {
      main = (
        <Broadcasts
          accountId={this.props.accountId}
          APIBaseURL={this.props.APIBaseURL}
          channels={this.props.channels}
          broadcastsData={this.props.broadcastsData}
          env={this.state.env}
          handleStudioURLChange={this.handleStudioURLChange}
          loadBroadcasts={this.props.loadBroadcasts}
          menuEnv={this.state.menuEnv}
          reAuth={this.props.reAuth}
          studioURLOverride={this.state.studioURLOverride}
          trackEvent={this.props.trackEvent}
        />
      );
    }
    return main;
  }
}

Dashboard.propTypes = {
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  APIBaseURL: PropTypes.string.isRequired,
  broadcastsData: broadcastsDataShape.isRequired,
  channels: PropTypes.arrayOf(channelItemShape).isRequired,
  checkAuthorization: PropTypes.func.isRequired,
  componentReady: PropTypes.bool.isRequired,
  getCookieValue: PropTypes.func.isRequired,
  history: routerHistoryShape.isRequired,
  loadBroadcasts: PropTypes.func.isRequired,
  location: routerLocationShape.isRequired,
  reAuth: PropTypes.func.isRequired,
  setGlobalAccountData: PropTypes.func.isRequired,
  setGlobalAccountId: PropTypes.func.isRequired,
  setGlobalAccountName: PropTypes.func.isRequired,
  setGlobalAccountRole: PropTypes.func.isRequired,
  setGlobalUserData: PropTypes.func.isRequired,
  trackEvent: PropTypes.func.isRequired,
  setAuthToken: PropTypes.func.isRequired,
};

export default connect(null, { setAuthToken })(Dashboard);
